/**
 * RemoteExecutor - Executes code via PHP backend API
 * 
 * Used for languages that can't run in the browser (Java, C++, C)
 * Calls the Wandbox API through our PHP backend for security.
 */

class RemoteExecutor {
  /**
   * Execute code remotely via PHP API
   * 
   * @param {string} apiEndpoint - The API endpoint URL
   * @param {string} language - The programming language (java, cpp, c)
   * @param {string} code - The code to execute
   * @param {string} stdin - Input to provide
   * @param {number} timeout - Maximum wait time in ms
   * @returns {Promise<{success: boolean, output: string, error: string|null, executionTime: number}>}
   */
  static async execute(apiEndpoint, language, code, stdin = '', timeout = 15000) {
    const startTime = Date.now();

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          language: language.toLowerCase(),
          code: code,
          stdin: stdin,
          timeout: Math.floor(timeout / 1000) // Convert to seconds for server
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle HTTP errors
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        return {
          success: false,
          output: '',
          error: this.parseHttpError(response.status, errorText),
          executionTime: Date.now() - startTime
        };
      }

      // Parse JSON response
      const result = await response.json();

      return {
        success: result.success !== false && !result.error,
        output: result.stdout || result.output || '',
        error: result.stderr || result.error || null,
        executionTime: result.executionTime || (Date.now() - startTime),
        exitCode: result.exitCode
      };

    } catch (err) {
      const executionTime = Date.now() - startTime;

      // Handle specific error types
      if (err.name === 'AbortError') {
        return {
          success: false,
          output: '',
          error: `Request timed out after ${timeout}ms. The server might be busy, try again.`,
          executionTime: executionTime
        };
      }

      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        return {
          success: false,
          output: '',
          error: 'Network error: Unable to connect to the code execution server. Check your internet connection.',
          executionTime: executionTime
        };
      }

      return {
        success: false,
        output: '',
        error: `Error: ${err.message}`,
        executionTime: executionTime
      };
    }
  }

  /**
   * Parse HTTP error codes into user-friendly messages
   */
  static parseHttpError(status, errorText) {
    switch (status) {
      case 400:
        return 'Bad request: The code or parameters were invalid.';
      case 401:
      case 403:
        return 'Access denied: You are not authorized to use this service.';
      case 404:
        return 'Service not found: The code execution API is not available.';
      case 413:
        return 'Code too large: Your code exceeds the maximum allowed size.';
      case 429:
        return 'Too many requests: Please wait a moment before trying again.';
      case 500:
        return 'Server error: The code execution server encountered an error.';
      case 502:
      case 503:
      case 504:
        return 'Server unavailable: The code execution service is temporarily down. Try again later.';
      default:
        return `Server error (${status}): ${errorText || 'Unknown error'}`;
    }
  }

  /**
   * Check if the remote API is available
   */
  static async checkHealth(apiEndpoint) {
    try {
      const healthUrl = apiEndpoint.replace('/execute.php', '/health.php');
      const response = await fetch(healthUrl, {
        method: 'GET',
        timeout: 5000
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get supported languages from the API
   */
  static async getSupportedLanguages(apiEndpoint) {
    try {
      const languagesUrl = apiEndpoint.replace('/execute.php', '/languages.php');
      const response = await fetch(languagesUrl);
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Check if executor is ready (depends on network)
   */
  static isReady() {
    return navigator.onLine;
  }
}

export default RemoteExecutor;